/**
 * useStaffEditForm (BON-398) — load + edit state for the staff detail page.
 *
 * Lifted out of StaffEditPage so the page component is just layout. Nothing
 * about the behaviour changed in the move; the diff logic itself lives in
 * `staffEditModel`, which is separately unit-tested.
 */
import { useCallback, useEffect, useState } from 'react';

import { fetchRegions, type RegionNode } from '../../api/regions';
import {
  fetchStaffMember,
  updateStaffProfile,
  StaffConflictError,
  StaffValidationError,
  type StaffMemberDetail,
  type StaffPatch,
} from '../../api/staff';

import { buildStaffPatch, formatHireDate } from './staffEditModel';

/** Fetches the member and the region tree the edit form needs. */
function useStaffLoad(id: string | undefined) {
  const [member, setMember] = useState<StaffMemberDetail | null>(null);
  const [regions, setRegions] = useState<RegionNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    // `stale` guards in-place id changes (staff A → staff B without an
    // unmount): a slow response for A must not overwrite B's member, or the
    // page would edit A's data and PATCH it to B's id.
    let stale = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([fetchStaffMember(id), fetchRegions()])
      .then(([m, r]) => {
        if (stale) return;
        setMember(m);
        setRegions(r);
      })
      .catch(() => {
        if (!stale) setLoadError('Failed to load staff member');
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [id]);

  // Pulls the record again after a change made outside the profile form, so
  // its edit history shows it. A failed refresh leaves the page as it was.
  const refreshMember = useCallback(() => {
    if (!id) return;
    fetchStaffMember(id).then(setMember, () => undefined);
  }, [id]);

  return { member, setMember, regions, loading, loadError, refreshMember };
}

/**
 * The member record's current value for a PATCH field. Only `hireDate` needs
 * care, normalised to the form's YYYY-MM-DD.
 *
 * BON-419 removed the second special case: the patch key used to be
 * `positions`, which resolved by name to the member's always-[] rate stub and
 * would have seeded the qualification picker empty — and, because the bridge is
 * replace-in-full, wiped every real grant on the next save. Now that the write
 * field is named `qualifiedPositions`, the generic lookup lands on the right
 * field on its own.
 */
function memberFieldValue<K extends keyof StaffPatch>(
  member: StaffMemberDetail,
  key: K,
): StaffPatch[K] | undefined {
  const raw = (member as unknown as Record<string, unknown>)[key as string];
  if (key === 'hireDate') return formatHireDate(raw) as StaffPatch[K];
  return raw as StaffPatch[K];
}

/**
 * Maps a failed save onto either per-field errors or a banner message.
 *
 * `zodErrorToFieldMap` files object-level issues under `_` rather than a field
 * name — chiefly `.strict()` rejecting an unrecognized key, which is what a
 * browser tab left open across a deploy sends. No control renders `_`, so
 * defaulting to "fix the highlighted fields" would highlight nothing and leave
 * the admin retrying a save that cannot succeed. Surface that message instead;
 * it names the offending key and tells them the page is stale.
 */
/** Shown beside the email field and in the banner when the backend refuses an
 *  email change. */
export const EMAIL_LOCKED_MESSAGE =
  'Email is locked once an invitation has been sent — it is the address their sign-in is bound to.';

function readSaveError(err: unknown): { fieldErrors: Record<string, string>; message: string } {
  if (err instanceof StaffConflictError && err.code === 'EMAIL_LOCKED') {
    return { fieldErrors: { email: EMAIL_LOCKED_MESSAGE }, message: EMAIL_LOCKED_MESSAGE };
  }
  if (err instanceof StaffValidationError) {
    const objectLevel = err.fieldErrors._;
    return {
      fieldErrors: err.fieldErrors,
      message: objectLevel ?? 'Please fix the highlighted fields and try again.',
    };
  }
  return { fieldErrors: {}, message: err instanceof Error ? err.message : 'Failed to save' };
}

export function useStaffEditForm(id: string | undefined) {
  const { member, setMember, regions, loading, loadError, refreshMember } = useStaffLoad(id);

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<StaffPatch>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const valueOf = useCallback(
    <K extends keyof StaffPatch>(key: K): StaffPatch[K] | undefined => {
      if (form[key] !== undefined) return form[key];
      return member ? memberFieldValue(member, key) : undefined;
    },
    [form, member],
  );

  const setField = useCallback(<K extends keyof StaffPatch>(key: K, value: StaffPatch[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const startEdit = useCallback(() => {
    setForm({});
    setSaveError(null);
    setFieldErrors({});
    setIsEditing(true);
  }, []);

  const cancelEdit = useCallback(() => {
    setForm({});
    setSaveError(null);
    setIsEditing(false);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!member || !id) return;
    const patch = buildStaffPatch(member, form);
    if (Object.keys(patch).length === 0) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    setSaveError(null);
    setFieldErrors({});
    try {
      const result = await updateStaffProfile(id, patch);
      setMember(result.staff);
      setForm({});
      setIsEditing(false);
    } catch (err) {
      const mapped = readSaveError(err);
      setFieldErrors(mapped.fieldErrors);
      setSaveError(mapped.message);
    } finally {
      setSaving(false);
    }
  }, [member, id, form, setMember]);

  return {
    member,
    regions,
    loading,
    loadError,
    isEditing,
    saving,
    saveError,
    fieldErrors,
    valueOf,
    setField,
    startEdit,
    cancelEdit,
    saveEdit,
    refreshMember,
  };
}
